import React, { useMemo } from 'react';
import type { Schedule, Task } from '../types';
import { formatUserTimezone, localTimeToUTC } from '../utils/timezone';

interface WeeklyCalendarProps {
  schedule: Schedule;
  reservations: Task[];  // Changed from Reservation[] to Task[]
  selectedDate: Date;
  onSlotClick: (date: Date, time: string) => void;
  onDateChange: (date: Date) => void;
  editingWorkingHours?: boolean;
  onTaskClick?: (task: Task) => void;  // New prop for task click
}

const WeeklyCalendar: React.FC<WeeklyCalendarProps> = ({
  schedule,
  reservations,
  selectedDate,
  onSlotClick,
  onDateChange,
  editingWorkingHours = false,
  onTaskClick,
}) => {
  // Get week dates (in local timezone)
  const weekDates = useMemo(() => {
    const dates: Date[] = [];
    
    // Get selected date in local timezone
    const selectedLocal = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate()
    );
    
    // Find Monday of the week (in local timezone)
    const day = selectedLocal.getDay();
    const diff = selectedLocal.getDate() - day + (day === 0 ? -6 : 1);
    selectedLocal.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const date = new Date(
        selectedLocal.getFullYear(),
        selectedLocal.getMonth(),
        selectedLocal.getDate() + i
      );
      dates.push(date);
    }
    return dates;
  }, [selectedDate]);

  // Debug: log reservations count and sample data
  React.useEffect(() => {
    if (reservations.length > 0) {
      console.log('WeeklyCalendar: reservations count:', reservations.length);
      
      // Log all reservations with dates in December 2025
      const decTasks = reservations.filter(res => {
        if (!res.start_time) return false;
        const date = new Date(res.start_time);
        return date.getFullYear() === 2025 && date.getMonth() === 11; // Month is 0-indexed, 11 = December
      });
      
      if (decTasks.length > 0) {
        console.log('WeeklyCalendar: December 2025 tasks:', decTasks.map(t => ({
          id: t.id,
          client_name: t.client_name,
          start_time: t.start_time,
          end_time: t.end_time,
          start_local: new Date(t.start_time).toLocaleString(),
          end_local: new Date(t.end_time).toLocaleString(),
        })));
      }
      
      console.log('WeeklyCalendar: sample reservation:', {
        id: reservations[0].id,
        client_name: reservations[0].client_name,
        start_time: reservations[0].start_time,
        end_time: reservations[0].end_time,
        start_time_parsed: new Date(reservations[0].start_time).toISOString(),
        end_time_parsed: new Date(reservations[0].end_time).toISOString(),
      });
    } else {
      console.log('WeeklyCalendar: no reservations');
    }
  }, [reservations]);
  
  // Debug: log week dates
  React.useEffect(() => {
    console.log('WeeklyCalendar: week dates:', weekDates.map(d => ({
      date: d.toLocaleDateString(),
      iso: d.toISOString(),
      day: d.getDate(),
      month: d.getMonth() + 1,
      year: d.getFullYear(),
    })));
  }, [weekDates]);

  // Generate time slots (commented out as not used)
  // const timeSlots = useMemo(() => {
  //   const slots: string[] = [];
  //   const slotDuration = schedule.slot_duration_minutes || 30;
  //   const slotsPerHour = 60 / slotDuration;

  //   for (let hour = 0; hour < 24; hour++) {
  //     for (let slot = 0; slot < slotsPerHour; slot++) {
  //       const minutes = slot * slotDuration;
  //       const timeStr = `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  //       slots.push(timeStr);
  //     }
  //   }
  //   return slots;
  // }, [schedule.slot_duration_minutes]);

  // Check if slot is booked and how many tasks overlap
  const getReservationsForSlot = (date: Date, localTime: string): { tasks: Task[], hasOverlap: boolean } => {
    if (!reservations || reservations.length === 0) {
      return { tasks: [], hasOverlap: false };
    }
    
    // Create a Date object for the local date/time (user's timezone)
    const [localHours, localMinutes] = localTime.split(':').map(Number);
    
    // Create date object in local timezone for the slot start
    // Important: Use date.getFullYear(), date.getMonth(), date.getDate() to ensure we use local date components
    const slotStartLocal = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      localHours,
      localMinutes,
      0,
      0
    );
    
    // Get UTC timestamp (getTime() returns UTC milliseconds)
    const slotStartUTCTimestamp = slotStartLocal.getTime();
    
    // Calculate slot end time in UTC (add slot duration)
    const slotEndUTCTimestamp = slotStartUTCTimestamp + ((schedule.slot_duration_minutes || 30) * 60 * 1000);

    const overlappingTasks = reservations.filter(res => {
      if (!res.start_time || !res.end_time) {
        return false;
      }
      
      // Parse reservation times (already in UTC from backend)
      const resStart = new Date(res.start_time);
      const resEnd = new Date(res.end_time);
      
      // Check for invalid dates
      if (isNaN(resStart.getTime()) || isNaN(resEnd.getTime())) {
        return false;
      }
      
      const resStartTime = resStart.getTime();
      const resEndTime = resEnd.getTime();
      
      // Check if reservation overlaps with this slot (all in UTC milliseconds)
      // Overlap condition: (resStart < slotEnd) AND (resEnd > slotStart)
      const overlaps = (
        resStartTime < slotEndUTCTimestamp &&
        resEndTime > slotStartUTCTimestamp
      );
      
      // Debug logging for December tasks
      const resDate = new Date(res.start_time);
      if (resDate.getFullYear() === 2025 && resDate.getMonth() === 11 && 
          (resDate.getDate() === 15 || resDate.getDate() === 17) &&
          date.getDate() === resDate.getDate() && date.getMonth() === 11 && date.getFullYear() === 2025 &&
          (localTime === '00:00' || localTime === '08:00')) {
        console.log('Overlap check for Dec task:', {
          taskId: res.id,
          taskName: res.client_name,
          slotDate: date.toLocaleDateString(),
          slotTime: localTime,
          slotStartUTC: new Date(slotStartUTCTimestamp).toISOString(),
          slotEndUTC: new Date(slotEndUTCTimestamp).toISOString(),
          resStartUTC: resStart.toISOString(),
          resEndUTC: resEnd.toISOString(),
          slotStartLocal: new Date(slotStartUTCTimestamp).toLocaleString(),
          resStartLocal: resStart.toLocaleString(),
          overlaps,
          comparison: {
            resStartLessThanSlotEnd: resStartTime < slotEndUTCTimestamp,
            resEndGreaterThanSlotStart: resEndTime > slotStartUTCTimestamp,
          }
        });
      }
      
      return overlaps;
    });
    
    return {
      tasks: overlappingTasks,
      hasOverlap: overlappingTasks.length > 1
    };
  };
  
  // Backward compatibility - get first reservation if exists
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getReservationForSlot = (date: Date, localTime: string): Task | null => {
    const result = getReservationsForSlot(date, localTime);
    return result.tasks.length > 0 ? result.tasks[0] : null;
  };

  // Check if slot is in working hours (TEMPLATE + EXCEPTIONS)
  const isInWorkingHours = (date: Date, localTime: string): boolean => {
    // Convert local time to UTC to find the correct UTC date and time
    const [localHours, localMinutes] = localTime.split(':').map(Number);
    
    // Create a date object for the local date/time
    const localDateTime = new Date(date);
    localDateTime.setHours(localHours, localMinutes, 0, 0);
    
    // Get UTC date and time components
    const utcDate = new Date(Date.UTC(
      localDateTime.getUTCFullYear(),
      localDateTime.getUTCMonth(),
      localDateTime.getUTCDate(),
      localDateTime.getUTCHours(),
      localDateTime.getUTCMinutes(),
      0,
      0
    ));
    
    // Get UTC date string (YYYY-MM-DD) - this might be different from local date if timezone crosses midnight
    const utcDateStr = utcDate.toISOString().split('T')[0];
    const utcTime = `${utcDate.getUTCHours().toString().padStart(2, '0')}:${utcDate.getUTCMinutes().toString().padStart(2, '0')}`;
    
    // First check exceptions (specific date overrides)
    // Exceptions are stored in UTC, so use UTC date and time
    const exceptions = (schedule as any).working_hours_exceptions || {};
    
    if (exceptions[utcDateStr] && utcTime in exceptions[utcDateStr]) {
      // Exception exists for this specific UTC date/time
      return exceptions[utcDateStr][utcTime];
    }

    // No exception - check template
    if (!schedule.working_hours) {
      return false; // If no template defined, nothing is available
    }

    // Handle different data formats
    let workingHoursArray: any[] = [];
    
    if (Array.isArray(schedule.working_hours)) {
      workingHoursArray = schedule.working_hours;
    } else if (typeof schedule.working_hours === 'string') {
      try {
        const parsed = JSON.parse(schedule.working_hours);
        workingHoursArray = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error('Error parsing working_hours JSON:', e);
        return false;
      }
    } else if (typeof schedule.working_hours === 'object') {
      // Handle object format (e.g., {monday: [...], tuesday: [...]})
      const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      workingHoursArray = dayNames.map((dayName, index) => {
        const dayData = (schedule.working_hours as any)[dayName];
        return {
          day: dayName.charAt(0).toUpperCase() + dayName.slice(1), // Capitalize first letter
          weekday: index,
          is_active: Array.isArray(dayData) && dayData.length > 0,
          time_ranges: Array.isArray(dayData) ? dayData : []
        };
      });
    }

    if (workingHoursArray.length === 0) {
      return false;
    }

    // Use UTC day of week to find the correct day config
    // UTC day might be different from local day if timezone crosses midnight
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const utcDayOfWeek = utcDate.getUTCDay(); // UTC day of week
    const utcDayName = dayNames[utcDayOfWeek === 0 ? 6 : utcDayOfWeek - 1]; // Convert Sunday=0 to Monday

    // Find day in template based on UTC day
    const dayConfig = workingHoursArray.find(
      (wh: any) => wh.day === utcDayName
    );

    if (!dayConfig || !dayConfig.is_active) {
      return false;
    }

    // Check if UTC time falls within any of the UTC time ranges for this day
    const [utcHours, utcMinutes] = utcTime.split(':').map(Number);
    const utcSlotMinutes = utcHours * 60 + utcMinutes;

    const result = dayConfig.time_ranges.some((range: any) => {
      // Time ranges are stored in UTC, so compare directly
      const [startHours, startMinutes] = range.start.split(':').map(Number);
      const startTotalMinutes = startHours * 60 + startMinutes;

      const [endHours, endMinutes] = range.end.split(':').map(Number);
      const endTotalMinutes = endHours * 60 + endMinutes;

      // Handle case where time range crosses midnight (e.g., 23:00 - 01:00)
      if (endTotalMinutes < startTotalMinutes) {
        // Range crosses midnight
        return utcSlotMinutes >= startTotalMinutes || utcSlotMinutes < endTotalMinutes;
      } else {
        // Normal range within same day
        return utcSlotMinutes >= startTotalMinutes && utcSlotMinutes < endTotalMinutes;
    }
    });

    return result;
  };

  // Navigate week
  const goToPreviousWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 7);
    onDateChange(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 7);
    onDateChange(newDate);
  };

  const goToToday = () => {
    onDateChange(new Date());
  };

  const formatDate = (date: Date): string => {
    // Use local date
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
  };

  const getDayName = (date: Date): string => {
    // Use local day
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  // Check if date is today (in local time)
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  // Check if time slot is current (in local time)
  const isCurrentTimeSlot = (date: Date, localTime: string): boolean => {
    if (!isToday(date)) return false;
    
    const now = new Date();
    const [hours, minutes] = localTime.split(':').map(Number);
    const slotTime = new Date(now);
    slotTime.setHours(hours, minutes, 0, 0);
    
    const nextSlot = new Date(slotTime);
    nextSlot.setMinutes(nextSlot.getMinutes() + schedule.slot_duration_minutes);
    
    // Current if now is within this slot
    return now >= slotTime && now < nextSlot;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Calendar Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={goToPreviousWeek}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={goToNextWeek}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            Today
          </button>
        </div>
        <div className="text-lg font-semibold text-gray-900">
          {formatDate(weekDates[0])} - {formatDate(weekDates[6])}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 250px)' }}>
        <div className="min-w-full">
          {/* Days Header */}
          <div className="grid grid-cols-8 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
            <div className="px-4 py-3 text-sm font-medium text-gray-700 border-r border-gray-200">
              Time
            </div>
            {weekDates.map((date, index) => {
              const isTodayDate = isToday(date);
              return (
              <div
                key={index}
                  className={`px-4 py-3 text-center border-r border-gray-200 last:border-r-0 ${
                    isTodayDate ? 'bg-primary-50 border-primary-300' : ''
                  }`}
              >
                <div className="text-sm font-medium text-gray-700">{getDayName(date)}</div>
                <div className={`text-lg font-semibold ${
                    isTodayDate
                      ? 'text-primary-700'
                    : 'text-gray-900'
                }`}>
                  {date.getDate()}
                </div>
              </div>
              );
            })}
          </div>

          {/* Time Slots - Each Hour Split in 2 */}
          {Array.from({ length: 24 }).map((_, hour) => {
            const hourStr = hour.toString().padStart(2, '0');
            const time00 = `${hourStr}:00`;
            const time30 = `${hourStr}:30`;
            
            return (
              <div key={hour} className="grid grid-cols-8 border-b-2 border-gray-300" style={{ minHeight: '60px' }}>
                {/* Time Label - Full Height (local time) */}
                <div className="px-4 py-2 text-sm text-gray-700 border-r border-gray-200 flex items-center justify-center font-medium">
                  <span>{hourStr}:00</span>
                </div>

                {/* Day Columns - Each Split in 2 Rows */}
                {weekDates.map((date, dayIndex) => (
                  <div key={dayIndex} className="border-r border-gray-200 last:border-r-0 flex flex-col">
                    {/* Top Half: :00-:30 */}
                    {(() => {
                      const localTime = time00;
                      const reservationResult = getReservationsForSlot(date, localTime);
                      const reservation = reservationResult.tasks.length > 0 ? reservationResult.tasks[0] : null;
                      const hasOverlappingTasks = reservationResult.hasOverlap;
                      const inWorkingHours = isInWorkingHours(date, localTime);
                      
                      // Check if past in local time
                      const now = new Date();
                      const slotDateTime = new Date(date);
                      const [hours, minutes] = localTime.split(':').map(Number);
                      slotDateTime.setHours(hours, minutes, 0, 0);
                      const isPast = slotDateTime < now;
                      const isCurrent = isCurrentTimeSlot(date, localTime);
                      
                      const isGoogleEvent = reservation?.source === 'google';

                      return (
                        <div
                          onClick={() => {
                            if (editingWorkingHours) {
                              // In editing mode, allow clicking any non-past slot
                              if (!isPast) {
                                // Convert local time to UTC for storage
                                const utcTime = localTimeToUTC(localTime, date);
                                onSlotClick(new Date(date), utcTime);
                              }
                            } else {
                              // In normal mode, only allow booking in working hours (not Google events)
                              if (!reservation && inWorkingHours && !isPast) {
                                // Convert local time to UTC for storage
                                const utcTime = localTimeToUTC(localTime, date);
                                onSlotClick(new Date(date), utcTime);
                              }
                            }
                          }}
                          className={`flex-1 border-b border-gray-200 relative ${
                            isCurrent
                              ? 'ring-2 ring-blue-400 ring-inset bg-blue-50'
                              : editingWorkingHours
                              ? isPast
                                ? 'bg-gray-50 cursor-not-allowed'
                                : inWorkingHours
                                ? 'bg-green-50 cursor-pointer hover:bg-green-100 transition'
                                : 'bg-gray-100 cursor-pointer hover:bg-gray-200 transition'
                              : !inWorkingHours
                              ? 'bg-gray-100 cursor-not-allowed'
                              : isPast
                              ? 'bg-gray-50 cursor-not-allowed'
                              : reservation
                              ? 'bg-purple-100 cursor-default'
                              : 'hover:bg-primary-50 cursor-pointer transition'
                          }`}
                        >
                          {reservation && (
                            <div 
                              className="absolute inset-0 p-1 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onTaskClick && !editingWorkingHours) {
                                  onTaskClick(reservation);
                                }
                              }}
                            >
                              {hasOverlappingTasks ? (
                                // Multiple overlapping tasks - show "Busy"
                                <div className="h-full rounded bg-purple-600 border border-purple-700 text-white p-1 text-xs overflow-hidden flex items-center justify-center hover:opacity-90 transition">
                                  <div className="font-semibold text-[11px] px-1">Busy</div>
                                </div>
                              ) : (
                                // Single task - show task name
                                <div className={`h-full rounded text-white p-1 text-xs overflow-hidden flex items-center justify-center hover:opacity-90 transition ${
                                  isGoogleEvent
                                    ? 'bg-purple-500 border border-purple-700'
                                    : 'bg-purple-500 border border-purple-700'
                                }`}>
                                  <div className="font-semibold truncate text-[11px] px-1">{reservation.client_name}</div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Bottom Half: :30-:00 */}
                    {(() => {
                      const localTime = time30;
                      const reservationResult = getReservationsForSlot(date, localTime);
                      const reservation = reservationResult.tasks.length > 0 ? reservationResult.tasks[0] : null;
                      const hasOverlappingTasks = reservationResult.hasOverlap;
                      const inWorkingHours = isInWorkingHours(date, localTime);
                      
                      // Check if past in local time
                      const now = new Date();
                      const slotDateTime = new Date(date);
                      const [hours, minutes] = localTime.split(':').map(Number);
                      slotDateTime.setHours(hours, minutes, 0, 0);
                      const isPast = slotDateTime < now;
                      const isCurrent = isCurrentTimeSlot(date, localTime);
                      
                      const isGoogleEvent = reservation?.source === 'google';

                      return (
                        <div
                          onClick={() => {
                            if (editingWorkingHours) {
                              // In editing mode, allow clicking any non-past slot
                              if (!isPast) {
                                // Convert local time to UTC for storage
                                const utcTime = localTimeToUTC(localTime, date);
                                onSlotClick(new Date(date), utcTime);
                              }
                            } else {
                              // In normal mode, only allow booking in working hours (not Google events)
                              if (!reservation && inWorkingHours && !isPast) {
                                // Convert local time to UTC for storage
                                const utcTime = localTimeToUTC(localTime, date);
                                onSlotClick(new Date(date), utcTime);
                              }
                            }
                          }}
                          className={`flex-1 relative ${
                            isCurrent
                              ? 'ring-2 ring-blue-400 ring-inset bg-blue-50'
                              : editingWorkingHours
                              ? isPast
                                ? 'bg-gray-50 cursor-not-allowed'
                                : inWorkingHours
                                ? 'bg-green-50 cursor-pointer hover:bg-green-100 transition'
                                : 'bg-gray-100 cursor-pointer hover:bg-gray-200 transition'
                              : !inWorkingHours
                              ? 'bg-gray-100 cursor-not-allowed'
                              : isPast
                              ? 'bg-gray-50 cursor-not-allowed'
                              : reservation
                              ? 'bg-purple-100 cursor-default'
                              : 'hover:bg-primary-50 cursor-pointer transition'
                          }`}
                        >
                          {reservation && (
                            <div 
                              className="absolute inset-0 p-1 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onTaskClick && !editingWorkingHours) {
                                  onTaskClick(reservation);
                                }
                              }}
                            >
                              {hasOverlappingTasks ? (
                                // Multiple overlapping tasks - show "Busy"
                                <div className="h-full rounded bg-purple-600 border border-purple-700 text-white p-1 text-xs overflow-hidden flex items-center justify-center hover:opacity-90 transition">
                                  <div className="font-semibold text-[11px] px-1">Busy</div>
                                </div>
                              ) : (
                                // Single task - show task name
                                <div className={`h-full rounded text-white p-1 text-xs overflow-hidden flex items-center justify-center hover:opacity-90 transition ${
                                  isGoogleEvent
                                    ? 'bg-purple-500 border border-purple-700'
                                    : 'bg-purple-500 border border-purple-700'
                                }`}>
                                  <div className="font-semibold truncate text-[11px] px-1">{reservation.client_name}</div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-600">
        <div className="flex items-center space-x-6">
          {!editingWorkingHours ? (
            <>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-purple-500 rounded mr-2 border border-purple-700"></div>
                <span>Booked</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-100 rounded mr-2 border border-gray-300"></div>
                <span>Outside working hours</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-white rounded mr-2 border border-gray-300"></div>
                <span>Available</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-blue-50 rounded mr-2 border-2 border-blue-400 ring-2 ring-blue-300"></div>
                <span>Current time</span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-50 rounded mr-2 border border-green-300"></div>
                <span>Available (click to block this date)</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-100 rounded mr-2 border border-gray-300"></div>
                <span>Blocked (click to unblock this date)</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-50 rounded mr-2 border border-gray-200"></div>
                <span>Past (cannot edit)</span>
              </div>
              <div className="text-orange-600 font-medium">
                📅 Each click changes only that specific date
              </div>
            </>
          )}
        </div>
        
        {/* Local Timezone Indicator */}
        <div className="flex items-center px-3 py-1 bg-primary-50 rounded-full border border-primary-200">
          <svg className="h-3 w-3 mr-1 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="font-semibold text-primary-700 text-xs">Local: {formatUserTimezone()}</span>
        </div>
      </div>
    </div>
  );
};

export default WeeklyCalendar;

